import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DollarSign, ShieldCheck, ExternalLink, AlertTriangle, CheckCircle2, LayoutDashboard, Lock } from 'lucide-react';
import PermissionGate from './PermissionGate';
import {
  fetchMonetizationConfigThunk,
  fetchConnectStatusThunk,
  fetchGroupPricingThunk,
  setGroupPricingThunk,
  onboardConnectThunk,
  subscribeToGroupThunk,
  fetchMySubscriptionThunk,
  cancelSubscriptionThunk,
} from '../../../redux/monetization/monetizationActionThunks';
import {
  selectMonetizationConfig,
  selectConnectStatus,
  selectGroupPricing,
  selectMySubscription,
  selectMonetizationLoading,
  selectMonetizationError,
} from '../../../redux/monetization/monetizationSlice';
import { fetchCredibilityThunk } from '../../../redux/credibility/credibilityActionThunks';
import { selectCredByUser } from '../../../redux/credibility/credibilitySlice';
import { guardrailStatus, formatPriceCeiling, GUARDRAIL_INFO, translateGuardrailLabel } from '../../../utils/groupGuardrailTiers';
import { InvestmentDisclaimerNotice } from '../../../components/Compliance/ComplianceNotices';

const formatPrice = (cents, currency) =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: (currency || 'eur').toUpperCase() }).format((cents || 0) / 100);

const GroupPricing = ({ groupId, isOwner }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const config = useSelector(selectMonetizationConfig);
  const connectStatus = useSelector(selectConnectStatus);
  const plan = useSelector((s) => selectGroupPricing(s, groupId));
  const mySubscription = useSelector((s) => selectMySubscription(s, groupId));
  const loading = useSelector(selectMonetizationLoading);
  const error = useSelector(selectMonetizationError);
  const currentUserId = useSelector((s) => s.auth?.user?.id);
  const ownCred = useSelector((s) => (currentUserId ? selectCredByUser(s, currentUserId) : null));

  const [form, setForm] = useState({ price_cents: 0, currency: 'eur', billing_type: 'free' });

  useEffect(() => {
    dispatch(fetchMonetizationConfigThunk());
    dispatch(fetchGroupPricingThunk(groupId));
    if (isOwner) dispatch(fetchConnectStatusThunk());
    if (!isOwner) dispatch(fetchMySubscriptionThunk(groupId));
  }, [dispatch, groupId, isOwner]);

  // Proportional guardrail status -- the owner's own tier determines their
  // price ceiling. Fetched here (soft=true so a never-scored owner gets the
  // cold-start placeholder instead of a 404) rather than assuming profile.jsx
  // already loaded it, since a group's Pricing tab can be the first screen
  // an owner visits in a session.
  useEffect(() => {
    if (isOwner && currentUserId) dispatch(fetchCredibilityThunk({ userId: currentUserId, soft: true }));
  }, [dispatch, isOwner, currentUserId]);

  const guardrail = useMemo(
    () => guardrailStatus(ownCred?.status_tier || 'Beginner', ownCred?.global_score || 0),
    [ownCred]
  );

  useEffect(() => {
    if (plan) {
      setForm({
        price_cents: plan.price_cents ?? 0,
        currency: plan.currency || 'eur',
        billing_type: plan.billing_type || 'free',
      });
    }
  }, [plan]);

  const priceExceedsCeiling =
    form.billing_type !== 'free' &&
    form.price_cents > 0 &&
    guardrail.priceCeilingCents !== null &&
    form.price_cents > guardrail.priceCeilingCents;

  const handleSave = () => {
    if (priceExceedsCeiling || guardrail.state === 'sandbox') return;
    dispatch(setGroupPricingThunk({ groupId, pricing: form }));
  };

  const handleOnboard = () => {
    dispatch(onboardConnectThunk());
  };

  const handleSubscribe = () => {
    dispatch(subscribeToGroupThunk(groupId));
  };

  const handleCancel = () => {
    dispatch(cancelSubscriptionThunk(groupId));
  };

  const isPaidPlan = plan && plan.billing_type !== 'free' && plan.price_cents > 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <DollarSign size={22} className="text-emerald-400" />
          {t('groups.pricing.heading')}
        </h2>
        {isOwner && (
          <button
            onClick={() => navigate('/creator/dashboard')}
            className="self-start sm:self-auto flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600/10 hover:bg-indigo-600/20 text-indigo-300 rounded-lg text-sm font-medium transition-colors border border-indigo-500/20"
          >
            <LayoutDashboard size={14} />
            {t('groups.pricing.creatorDashboard')}
          </button>
        )}
      </div>

      {!config.monetization_live && (
        <div className="flex items-start gap-3 p-4 bg-amber-100 dark:bg-amber-500/10 border border-amber-500/30 rounded-xl">
          <AlertTriangle size={18} className="text-amber-700 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-300">{t('groups.pricing.testModeTitle')}</p>
            <p className="text-xs text-amber-400/80 mt-1">
              {t('groups.pricing.testModeBody')}
            </p>
          </div>
        </div>
      )}

      {/* ---------------- OWNER VIEW: pricing editor + Connect onboarding ---------------- */}
      <PermissionGate isOwner={isOwner} requireOwner>
        <div className="bg-cream-100 dark:bg-white/5 border border-cream-300 dark:border-white/10 rounded-2xl p-6 space-y-6">
          <div>
            <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-300 mb-3 flex items-center gap-2">
              <ShieldCheck size={16} className="text-blue-400" />
              {t('groups.pricing.creatorPayoutSetup')}
            </h3>
            {connectStatus.payouts_enabled ? (
              <p className="text-sm text-emerald-400 flex items-center gap-2">
                <CheckCircle2 size={16} /> {t('groups.pricing.payoutsEnabled')}
              </p>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {connectStatus.onboarding_complete
                    ? t('groups.pricing.onboardingSubmitted')
                    : t('groups.pricing.completeOnboarding')}
                </p>
                <button
                  onClick={handleOnboard}
                  disabled={loading}
                  className="px-4 py-2 bg-gradient-to-r from-violet-500 to-indigo-600 hover:from-violet-600 hover:to-indigo-700 text-white rounded-xl flex items-center gap-2 text-sm disabled:opacity-50"
                >
                  <ExternalLink size={14} />
                  {connectStatus.onboarding_complete ? t('groups.pricing.resumeOnboarding') : t('groups.pricing.startPayoutSetup')}
                </button>
              </div>
            )}
          </div>

          <div className="pt-4 border-t border-gray-200 dark:border-gray-700/50 space-y-4">
            <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-300">{t('groups.pricing.plan')}</h3>

            {/* States plainly what a subscriber is actually paying for
                (community access) alongside what it isn't (advice) --
                reinforces the same framing the owner's own subscribers see
                on the subscribe view below. */}
            <InvestmentDisclaimerNotice context="subscription" />

            {/* Proportional guardrail status -- your own credibility tier
                sets your price ceiling, not a one-time approval. Reaching a
                higher tier raises it automatically, no re-application. */}
            <div className="flex items-start gap-3 p-4 bg-blue-500/5 border border-blue-500/20 rounded-xl">
              <ShieldCheck size={18} className="text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="text-blue-300 font-medium">
                  {t('groups.pricing.yourTier', { label: translateGuardrailLabel(guardrail.state, t), tier: ownCred?.status_tier || 'Beginner' })}
                </p>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  {t('groups.pricing.priceCeiling')} <span className="text-gray-900 dark:text-white font-medium">{formatPriceCeiling(guardrail.priceCeilingCents, t)}</span>
                  {guardrail.nextState && (
                    <> · {guardrail.pointsToNextState > 0
                      ? t('groups.pricing.moreCreditPointsReaches', { points: guardrail.pointsToNextState, nextLabel: translateGuardrailLabel(guardrail.nextState, t), nextCeiling: formatPriceCeiling(GUARDRAIL_INFO[guardrail.nextState]?.priceCeilingCents, t) })
                      : t('groups.pricing.eligibleForNextState', { nextLabel: translateGuardrailLabel(guardrail.nextState, t) })}</>
                  )}
                </p>
              </div>
            </div>

            {guardrail.state === 'sandbox' ? (
              <div className="flex items-start gap-3 p-4 bg-gray-100 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700/50 rounded-xl">
                <Lock size={18} className="text-gray-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('groups.pricing.sandboxNotice')}
                </p>
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">{t('groups.pricing.billingType')}</label>
                  <select
                    value={form.billing_type}
                    onChange={(e) => setForm({ ...form, billing_type: e.target.value })}
                    className="w-full bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="free">{t('groups.pricing.billingFree')}</option>
                    <option value="monthly">{t('groups.pricing.billingMonthly')}</option>
                    <option value="one_time">{t('groups.pricing.billingOneTime')}</option>
                  </select>
                </div>

                {form.billing_type !== 'free' && (
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">{t('groups.pricing.price')}</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={(form.price_cents / 100).toFixed(2)}
                        onChange={(e) => setForm({ ...form, price_cents: Math.round(parseFloat(e.target.value || '0') * 100) })}
                        className={`w-full bg-white dark:bg-gray-900 border text-gray-900 dark:text-white px-4 py-3 rounded-xl focus:outline-none focus:ring-2 ${
                          priceExceedsCeiling ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'
                        }`}
                      />
                      {priceExceedsCeiling && (
                        <p className="text-xs text-red-400 mt-1.5">
                          {t('groups.pricing.exceedsCeiling', { label: translateGuardrailLabel(guardrail.state, t), ceiling: formatPriceCeiling(guardrail.priceCeilingCents, t) })}
                        </p>
                      )}
                    </div>
                    <div className="w-28">
                      <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">{t('groups.pricing.currency')}</label>
                      <select
                        value={form.currency}
                        onChange={(e) => setForm({ ...form, currency: e.target.value })}
                        className="w-full bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="eur">EUR</option>
                      </select>
                    </div>
                  </div>
                )}

                {error && <p className="text-sm text-red-400">{error}</p>}

                <button
                  onClick={handleSave}
                  disabled={loading || priceExceedsCeiling}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl disabled:opacity-50 text-sm"
                >
                  {loading ? t('groups.pricing.saving') : t('groups.pricing.savePricing')}
                </button>
              </>
            )}
          </div>
        </div>
      </PermissionGate>

      {/* ---------------- MEMBER VIEW: subscribe / cancel ---------------- */}
      {!isOwner && (
        <div className="bg-cream-100 dark:bg-white/5 border border-cream-300 dark:border-white/10 rounded-2xl p-6 space-y-4">
          {!isPaidPlan ? (
            <p className="text-gray-600 dark:text-gray-300">{t('groups.pricing.freeToJoin')}</p>
          ) : (
            <>
              {/* The exact "paying for a credible person's opinions" moment
                  Technical_Docs/28_EU_REGULATORY_COMPLIANCE_FORWARD_PATH.md's
                  addendum flagged as having zero disclaimer -- fixed here. */}
              <InvestmentDisclaimerNotice context="subscription" />
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-900 dark:text-white text-lg font-semibold">{formatPrice(plan.price_cents, plan.currency)}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {plan.billing_type === 'monthly' ? t('groups.pricing.perMonth') : t('groups.pricing.oneTimeAccess')}
                  </p>
                </div>
                {mySubscription?.status === 'active' ? (
                  <button
                    onClick={handleCancel}
                    disabled={loading}
                    className="px-4 py-2 bg-red-600/20 hover:bg-red-600/30 border border-red-500/30 text-red-300 rounded-xl text-sm disabled:opacity-50"
                  >
                    {t('groups.pricing.cancelSubscription')}
                  </button>
                ) : (
                  <button
                    onClick={handleSubscribe}
                    disabled={loading}
                    className="px-5 py-2.5 bg-gradient-to-r from-violet-500 to-indigo-600 hover:from-violet-600 hover:to-indigo-700 text-white rounded-xl text-sm font-medium disabled:opacity-50"
                  >
                    {t('groups.pricing.subscribe')}
                  </button>
                )}
              </div>
              {mySubscription?.status === 'active' && mySubscription.current_period_end && (
                <p className="text-xs text-gray-500 dark:text-gray-500">
                  {mySubscription.status === 'canceled'
                    ? t('groups.pricing.accessEndsOn', { date: new Date(mySubscription.current_period_end).toLocaleDateString() })
                    : t('groups.pricing.renewsOn', { date: new Date(mySubscription.current_period_end).toLocaleDateString() })}
                </p>
              )}
              {error && <p className="text-sm text-red-400">{error}</p>}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default GroupPricing;
